import { apiFetch } from './client.js';

/** Equivalencia aproximada token ↔ operación (tablero / staff). */
export interface TokenEquivalence {
  id: string;
  label: string;
  tokens: number;
  tokens_max?: number;
}

/** Recarga comprable (Recarga 400 `kit_3m`, 1,000, 2,500 y 5,000). */
export interface TokenKit {
  id: string;
  label: string;
  tokens: number;
  /** tokens / 10,000 (aditivo; ausente en un API viejo → se calcula). */
  creditos?: number;
  amount: number | null;
  currency: string;
}

/** Unidad visible. `activo` = CREDITOS_ENABLED del servicio (con false todo sigue en tokens). */
export interface UnidadInfo {
  nombre: 'créditos';
  tokens_por_credito: number;
  activo: boolean;
  desde?: string;
}

/** Bloque `creditos` de /usage: números con decimales; el cliente formatea. */
export interface CreditosBloque {
  bolsa: number | null;
  usado: number;
  recargas: number;
  disponible: number | null;
}

export interface PromedioApi {
  id: string;
  label: string;
  unidad: string;
  tokens: number;
  creditos: number;
  creditos_aprox: number;
  texto: string;
}

export interface UsageResponse {
  period: string;
  plan: 'start' | 'pro' | 'teams' | 'vitruvio';
  plan_display: string;
  counters: {
    projects_created: number;
    chats_sent: number;
    concepts_quoted: number;
    apus_generated: number;
    catalogs_active: number;
  };
  limits: {
    projects_created: number | null;
    chats_sent: number | null;
    concepts_quoted: number | null;
    apus_generated: number | null;
    catalogs_active: number | null;
  };
  /** Límite efectivo: IA = null (no-enforce); proyectos/catálogos sí. */
  effective_limits?: {
    projects_created: number | null;
    chats_sent: number | null;
    concepts_quoted: number | null;
    apus_generated: number | null;
    catalogs_active: number | null;
  };
  /** Saldo legacy de recargas por counter (compat; ya no se vende). */
  packs?: {
    concepts_quoted: number;
    apus_generated: number;
    chats_sent: number;
  };
  /** Orden real: bolsa mensual → kits (pack_tokens). */
  consumption_order?: ReadonlyArray<'monthly_bag' | 'pack_tokens' | 'plan_base' | 'packs' | 'weekly_bag'>;
  /** Equivalencia de bolsa llena del plan (para escalar al restante). */
  plan_equivalence?: {
    concepts: number;
    apus: number;
    chats: number;
    tokens_month: number;
    /** Compat lectura si un cliente viejo aún manda tokens_week. */
    tokens_week?: number;
  };
  seats?: number;
  team_breakdown?: Array<{
    user_id: string;
    email: string | null;
    name: string | null;
    concepts_quoted: number;
    apus_generated: number;
    chats_sent: number;
  }>;
  tokens: {
    used: number;
    quota: number;
    remaining: number;
    /** Kits comprados pendientes (se consumen después de la bolsa mensual). */
    pack_tokens?: number;
    month_start?: string | null;
    month_end?: string | null;
    /** Vencimiento del plan de IA (ISO). Las recargas vencen junto con el plan. */
    plan_expires_at?: string | null;
    breakdown?: { propia: number; externa: number } | null;
  };
  equivalencias: {
    items: TokenEquivalence[];
    note: string;
  };
  kits: TokenKit[];
  /** Créditos (aditivo). Ausentes en un API viejo: la UI sigue en tokens. */
  unidad?: UnidadInfo;
  creditos?: CreditosBloque;
  factores?: Record<string, number>;
  cobro?: { activo: boolean };
  promedios?: PromedioApi[];
  panteon?: {
    id: string;
    nombre: string;
    min: number;
    max: number;
    paso: number;
    tramos: Array<{ desde: number; hasta: number; precio_credito: number }>;
  };
  storage?: {
    used_bytes: number;
    quota_bytes: number;
    scope: 'user' | 'tenant';
    breakdown: {
      project_files: number;
      quote_excels: number;
      custom_dbs: number;
      catalogs: number;
      exports: number;
    };
  };
}

export function getUsage(): Promise<UsageResponse> {
  return apiFetch<UsageResponse>('/api/v1/usage');
}

/** Detalle del popover "Tus créditos" (eko27 vía API). Sin nombres de modelo. */
export interface CreditosDetalle {
  ok?: boolean;
  unidad?: UnidadInfo;
  plan?: { id: string; label: string };
  month?: string;
  renueva?: string;
  sin_limite?: boolean;
  tokens?: { bolsa: number | null; usado: number; recargas: number; disponible: number | null };
  por_funcion?: Array<{ id: string; label: string; tokens: number; creditos: number }>;
  por_dia?: Array<{ dia: string; tokens: number; creditos: number }>;
  recargas?: Array<{ fecha: string; tokens: number; creditos: number; etiqueta?: string }>;
  detalle?: null;
}

export function getCreditosDetalle(): Promise<CreditosDetalle> {
  return apiFetch<CreditosDetalle>('/api/v1/usage/creditos');
}

/** Etiqueta corta (Start/Pro/Teams). Prefiere `plan_display` de eko27. */
export function planShortLabel(usage: Pick<UsageResponse, 'plan' | 'plan_display'> | null | undefined): string {
  const labels: Record<string, string> = { start: 'Start', pro: 'Pro', teams: 'Teams', vitruvio: 'Vitruvio' };
  const display = usage?.plan_display?.trim();
  if (display) {
    const stripped = display.replace(/^OPUS\s+/i, '').trim();
    if (stripped) return stripped;
  }
  return labels[usage?.plan ?? 'start'] ?? 'Start';
}

/** % de uso de la bolsa mensual de tokens (0–100). */
export function tokensPct(usage: UsageResponse | null | undefined): number {
  if (!usage?.tokens) return 0;
  const { used, quota } = usage.tokens;
  if (!quota || quota <= 0) return 0;
  return Math.min(100, Math.round((used / quota) * 100));
}

/** Tokens restantes del mes (sin contar kits). */
export function monthRemaining(usage: UsageResponse | null | undefined): number {
  if (!usage?.tokens) return 0;
  const { used, quota } = usage.tokens;
  return Math.max(0, (quota ?? 0) - (used ?? 0));
}
